import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export function getOptionValue(option) {
  if (option && typeof option === 'object') return option.value;
  return option;
}

function getOptionLabel(option) {
  if (option && typeof option === 'object') return option.label || option.value;
  return option;
}

export function getSelectedOptionLabel(options, selectedValue, fallback = '') {
  const selected = (Array.isArray(options) ? options : []).find(
    (option) => getOptionValue(option) === selectedValue
  );
  return selected ? getOptionLabel(selected) : (selectedValue || fallback);
}

export default function ModernTimeDropdownV2({ placeholder, value, onChange, options = [], width='w-56', height='w-8' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  
  // Ensure options is always an array
  const safeOptions = Array.isArray(options) ? options : [];
  
  // Filter options based on search term
  const filteredOptions = safeOptions.filter((option) => {
    const searchValue = searchTerm.toLowerCase();
    return String(getOptionLabel(option) || '').toLowerCase().includes(searchValue)
      || String(getOptionValue(option) || '').toLowerCase().includes(searchValue);
  });
  
  const shouldShowSearch = safeOptions.length > 5;
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    setSearchTerm(''); // Reset search when toggling
  };

  const handleSelection = (option) => {
    onChange({ target: { value: getOptionValue(option) } });
    setIsOpen(false);
    setSearchTerm(''); // Reset search after selection
  };
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm(''); // Reset search when closing
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && shouldShowSearch && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, shouldShowSearch]);

  return (
    <div className={`relative ${width}`} ref={dropdownRef}>
      <button
        type="button"
        className={`flex items-center justify-between w-full px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-sm hover:bg-gray-50 focus:outline-none w-8 text-sm ${height}`}
        onClick={toggleDropdown} style={{height: "42px",paddingLeft: "0.55rem",borderRadius: "4px", width: "13.5rem"}}
      >
        <span className="truncate" style={{fontSize: "15px"}} title={getSelectedOptionLabel(safeOptions, value, placeholder)}>
          {getSelectedOptionLabel(safeOptions, value, placeholder)}
        </span>
        <div>
        <ChevronDown className={`w-4 h-3 ml-2 transition-transform duration-200 rotate-180`} style={{marginBottom: "-3px"}} />
        <ChevronDown className={`w-4 h-3 ml-2 transition-transform duration-200`} />
        </div>
      </button>
      
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          {shouldShowSearch && (
            <div className="px-3 py-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search"
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onClick={(e) => e.stopPropagation()} style={{borderRadius: "4px"}}
                />
              </div>
            </div>
          )}
          <ul className="py-1 overflow-auto text-sm max-h-60">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <li
                  key={`${getOptionValue(option)}-${index}`}
                  className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                    getOptionValue(option) === value ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-700'
                  }`}
                  onClick={() => handleSelection(option)}
                >
                  <div>{getOptionLabel(option)}</div>
                  {!option?.hideId && getOptionLabel(option) !== getOptionValue(option) && (
                    <div className="text-xs text-gray-500 font-normal">ID: {getOptionValue(option)}</div>
                  )}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500 text-center">
                No options found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
